import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * Post-Pitch "You saw the pitches. Now come experience the Shower." campaign admin card.
 * Pulls recipient counts (auto-seeded from livestream_access signups on first status call),
 * lets admin: preview email, send to all eligible, add extra contacts, suppress people.
 */
export default function PostPitchCampaignCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [sendOnAdd, setSendOnAdd] = useState(true);

  const fetch_ = async () => {
    try {
      const r = await api.get("/admin/postpitch/status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    fetch_();
    const t = setInterval(fetch_, 60000);
    return () => clearInterval(t);
  }, []);

  const preview = async () => {
    const to = window.prompt("Preview the post-pitch email — send a copy to which address?", "info@itsthefreedominme.com");
    if (!to) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/postpitch/send?dry_run_to=${encodeURIComponent(to)}`);
      alert(`✅ Preview sent to ${to}\n\n${r.data.sent} sent · ${r.data.failed} failed`);
    } catch (e) {
      alert(`❌ Failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const sendAll = async () => {
    if (!window.confirm(`Send the "You saw the pitches" email to all ${status?.eligible_now ?? "?"} eligible contacts right now?\n\nThis blasts to everyone who registered for the pitch livestreams and hasn't received it yet. Suppressed contacts are skipped.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/postpitch/send");
      alert(`✅ Sent ${r.data.sent} · Failed ${r.data.failed}`);
      await fetch_();
    } catch (e) {
      alert(`❌ Send failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const addContact = async (e) => {
    e?.preventDefault?.();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { alert("Please enter a valid email."); return; }
    setBusy(true);
    try {
      const params = new URLSearchParams({ email, name: newName.trim(), phone: newPhone.trim(), send_now: sendOnAdd ? "true" : "false" });
      const r = await api.post(`/admin/postpitch/add-contact?${params.toString()}`);
      if (r.data.status === "exists") {
        alert(`⚠️ ${email} is already in the post-pitch list.`);
      } else {
        const sentLine = r.data.email_sent?.sent ? "\n📧 Email sent immediately." : "";
        alert(`✅ Added ${email}${sentLine}`);
        setNewName(""); setNewEmail(""); setNewPhone("");
      }
      await fetch_();
    } catch (err) {
      alert(`❌ Failed: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  const suppress = async () => {
    const email = window.prompt("Suppress (stop post-pitch emails to) which email address?");
    if (!email) return;
    try {
      const r = await api.post(`/admin/postpitch/suppress?email=${encodeURIComponent(email)}`);
      alert(r.data.suppressed ? `✅ ${email} suppressed` : `⚠️ No contact found for ${email}`);
      await fetch_();
    } catch (e) { alert(`❌ Failed: ${e?.response?.data?.detail || e.message}`); }
  };

  if (!status) return null;

  return (
    <div data-testid="postpitch-card" className="rounded-2xl border border-amber-100 bg-white shadow-sm mb-6 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-amber-50 bg-gradient-to-r from-amber-50/60 via-white to-rose-50/40">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold font-[Outfit] px-2.5 py-1 rounded-full border bg-amber-100 text-amber-800 border-amber-300">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
            Post-Pitch Campaign
          </span>
          <span className="font-[Outfit] text-sm text-emerald-950 font-semibold">&ldquo;You saw the pitches — now come experience the Shower&rdquo;</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="postpitch-add-btn" onClick={() => setAddOpen((v) => !v)} disabled={busy} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-[Outfit] h-8 text-xs">+ Add Contact</Button>
          <Button data-testid="postpitch-preview-btn" onClick={preview} disabled={busy} variant="outline" className="rounded-full border-emerald-300 text-emerald-800 hover:bg-emerald-50 font-[Outfit] h-8 text-xs">Preview to Me</Button>
          <Button data-testid="postpitch-send-btn" onClick={sendAll} disabled={busy || (status.eligible_now || 0) === 0} className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-[Outfit] h-8 text-xs">
            {status.eligible_now > 0 ? `Send to ${status.eligible_now}` : "All Sent"}
          </Button>
          <Button data-testid="postpitch-suppress-btn" onClick={suppress} variant="outline" className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 font-[Outfit] h-8 text-xs">Suppress</Button>
        </div>
      </div>

      {addOpen && (
        <form onSubmit={addContact} data-testid="postpitch-add-form" className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_120px_auto] gap-2 items-center px-5 py-3 bg-emerald-50/60 border-b border-emerald-100">
          <input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@example.com *" data-testid="postpitch-add-email" className="rounded-lg border border-emerald-300 px-3 py-2 text-sm font-[Outfit] bg-white focus:outline-none focus:border-emerald-500" />
          <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name (optional)" data-testid="postpitch-add-name" className="rounded-lg border border-emerald-300 px-3 py-2 text-sm font-[Outfit] bg-white focus:outline-none focus:border-emerald-500" />
          <input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Phone (optional)" data-testid="postpitch-add-phone" className="rounded-lg border border-emerald-300 px-3 py-2 text-sm font-[Outfit] bg-white focus:outline-none focus:border-emerald-500" />
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs font-[Outfit] text-emerald-800 cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={sendOnAdd} onChange={(e) => setSendOnAdd(e.target.checked)} className="accent-emerald-700" />
              Send now
            </label>
            <Button type="submit" disabled={busy} data-testid="postpitch-add-submit" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-[Outfit] h-8 text-xs">{busy ? "Adding…" : "Add"}</Button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-amber-50 border-b border-amber-50">
        <Stat label="Total" value={status.total} testid="postpitch-stat-total" />
        <Stat label="Eligible Now" value={status.eligible_now} testid="postpitch-stat-eligible" highlight />
        <Stat label="Already Sent" value={status.already_sent} testid="postpitch-stat-sent" />
        <Stat label="Suppressed" value={status.suppressed} testid="postpitch-stat-suppressed" />
      </div>

      <div className="px-5 py-2.5 bg-amber-50/40 text-[11px] text-amber-900 font-[Outfit]">
        💡 Recipient list auto-syncs from anyone who registered for the pitch livestreams. Use <b>+ Add Contact</b> to add anyone else (someone who watched a replay, an alumni, a supporter). Suppression is per-campaign.
      </div>
    </div>
  );
}

function Stat({ label, value, testid, highlight }) {
  return (
    <div data-testid={testid} className={`p-4 ${highlight ? "bg-rose-50/40" : "bg-white"}`}>
      <div className="font-[Outfit] text-[10px] uppercase tracking-[0.18em] text-emerald-900/70 font-bold">{label}</div>
      <div className={`font-[Cormorant_Garamond] text-3xl mt-1 tabular-nums ${highlight ? "text-rose-600" : "text-emerald-950"}`}>{value ?? 0}</div>
    </div>
  );
}
